/**
 * Iurop Quick Add — product list "+" button.
 *
 * Behavior:
 *   - Single-variant products: click "+" → adds variant to cart.
 *   - Products with a size option:
 *       · Desktop (hover-capable, ≥ mobile bp): hovering the card or clicking
 *         "+" reveals the popover with size buttons. Clicking a size adds it.
 *       · Mobile (no hover / ≤ mobile bp): clicking "+" opens the popover as
 *         a bottom-sheet modal with a backdrop.
 *
 * Wires into the existing cart drawer via the global `cart:updated` event.
 */
(function () {
  'use strict';

  const MOBILE_BREAKPOINT = 750;
  const isMobileViewport = () => window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches;

  let backdrop = null;
  let openPopover = null;

  const ensureBackdrop = () => {
    if (backdrop) return backdrop;
    backdrop = document.createElement('div');
    backdrop.className = 'iurop-quick-add-backdrop';
    backdrop.addEventListener('click', closeOpenPopover);
    document.body.appendChild(backdrop);
    return backdrop;
  };

  const openPopoverEl = (popover, trigger) => {
    closeOpenPopover();
    popover.hidden = false;
    popover.classList.add('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    let portal = null;
    if (isMobileViewport()) {
      // Portal the popover to <body> so it escapes the product-list's
      // isolated stacking context (z-index:0; isolation:isolate) and can
      // sit above the backdrop.
      portal = { parent: popover.parentNode, next: popover.nextSibling };
      document.body.appendChild(popover);
      ensureBackdrop().classList.add('is-open');
      document.body.style.overflow = 'hidden';
      document.body.classList.add('quick-add-open');
    }

    openPopover = { popover, trigger, portal };
  };

  const closeOpenPopover = () => {
    if (!openPopover) return;
    const { popover, trigger, portal } = openPopover;
    popover.classList.remove('is-open');
    popover.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (portal && portal.parent) {
      portal.parent.insertBefore(popover, portal.next || null);
    }
    if (backdrop) backdrop.classList.remove('is-open');
    document.body.style.overflow = '';
    document.body.classList.remove('quick-add-open');
    openPopover = null;
  };

  const addToCart = (variantId, trigger) => {
    if (!variantId) return Promise.reject(new Error('missing-variant-id'));
    if (trigger) trigger.classList.add('is-loading');

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then((r) => (r.ok ? r.json() : r.json().then((e) => Promise.reject(e))))
      .then((item) => fetch('/cart.js').then((r) => r.json()).then((cart) => ({ item, cart })))
      .then(({ item, cart }) => {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: item }));
        if (trigger) {
          trigger.classList.remove('is-loading');
          trigger.classList.add('is-added');
          setTimeout(() => trigger.classList.remove('is-added'), 1200);
        }
      })
      .catch((err) => {
        console.error('[quick-add] add to cart failed', err);
        if (trigger) trigger.classList.remove('is-loading');
        const message = (err && err.description) || 'No se pudo añadir al carrito';
        window.alert(message);
      });
  };

  // ------------------------------------------------------------------
  // Trigger ("+") click handler
  // ------------------------------------------------------------------
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-quick-add-trigger]');
    const sizeBtn = e.target.closest('[data-quick-add-size]');

    // Click on a size in the popover → add variant.
    if (sizeBtn) {
      e.preventDefault();
      e.stopPropagation();
      if (sizeBtn.disabled) return;
      const variantId = sizeBtn.dataset.variantId;
      // When portaled to <body> on mobile, the popover is no longer inside the
      // card — fall back to the trigger we stored when opening.
      const card = sizeBtn.closest('[data-product-card]');
      const cardTrigger =
        (card && card.querySelector('[data-quick-add-trigger]')) ||
        (openPopover && openPopover.trigger) ||
        null;
      addToCart(variantId, cardTrigger).then(() => closeOpenPopover());
      return;
    }

    // Click on "+" trigger.
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();

      const mode = trigger.dataset.quickAddMode;
      if (mode === 'single') {
        addToCart(trigger.dataset.variantId, trigger);
        return;
      }

      if (mode === 'sizes') {
        const card = trigger.closest('[data-product-card]');
        const popover = card ? card.querySelector('[data-quick-add-popover]') : null;
        if (!popover) return;
        if (openPopover && openPopover.popover === popover) {
          closeOpenPopover();
        } else {
          openPopoverEl(popover, trigger);
        }
      }
      return;
    }

    // Click outside any popover closes it.
    if (openPopover && !e.target.closest('[data-quick-add-popover]')) {
      closeOpenPopover();
    }
  });

  // Close on Escape.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && openPopover) closeOpenPopover();
  });

  // Close the popover if the viewport crosses the breakpoint while open.
  // The popover may have been portaled to <body>, so the safest reset is
  // to fully close (which restores it to its original parent).
  window.addEventListener('resize', () => {
    if (openPopover && !isMobileViewport()) {
      closeOpenPopover();
    }
  });
})();
